import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const currentPath = window.location.pathname;

    console.error('ErrorBoundary caught an error:', error);
    console.error('Error info:', errorInfo);
    console.error('Current path:', currentPath);

    // Report to Sentry with component stack context
    Sentry.withScope((scope) => {
      scope.setTag('page_path', currentPath);
      scope.setExtra('componentStack', errorInfo.componentStack);
      Sentry.captureException(error);
    });

    if (typeof window !== 'undefined' && (window as any).posthog) {
      const posthog = (window as any).posthog;
      posthog.capture('react_error_boundary_triggered', {
        page_path: currentPath,
        error_message: error?.message || 'Unknown error',
        error_name: error?.name || 'Error',
        error_stack: error?.stack || '',
        component_stack: errorInfo.componentStack,
        is_426_error: error?.message?.includes('426') || error?.message?.includes('Minified React error #426') || false,
        is_suspense_error: error?.message?.includes('Suspense') || false,
        is_mobile: /Android|iPhone|iPad/i.test(navigator.userAgent),
        is_facebook_browser: /FBAN|FBAV/i.test(navigator.userAgent),
        user_agent: navigator.userAgent,
      });
    }
  }

  private handleReset = () => {
    console.log('ErrorBoundary: Reloading page at', window.location.pathname);
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-bg flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold text-white mb-4">Something went wrong</h1>
            <p className="text-white/70 mb-6">
              We encountered an unexpected error. Please try reloading the page.
            </p>
            <button
              onClick={this.handleReset}
              className="bg-accent text-white btn-glow px-6 py-3 rounded-full font-semibold hover:bg-accent-hover transition-colors min-h-[44px]"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
